import { CURRICULUM_PHASES, type CurriculumPhase, phaseRank } from "@curriculum-studio/core";
import { PHASE_LABEL } from "./tag-chips.tsx";

// The phase tracker — ONE component, rendered identically on the index and on a document's own page, so
// the two surfaces cannot drift into different visual languages for the same fact.
//
// One circle per production stage. A circle's colour is its STATE, never which stage it is:
//   grey outline = not started · sky = in progress · filled green tick = done · dashed amber = returned to
// A published document therefore reads as an unbroken green chain, and a live document with part of it
// back at an earlier stage shows a single dashed amber circle among the green — which is the whole
// "published, but reworking structure" story without a word of prose. Because amber only ever means
// "re-entered", it can never be mistaken for a completed step.

export type StepState = "todo" | "now" | "done" | "back";

export const stepStateOf = (
  index: number,
  phase: CurriculumPhase,
  reworkPhase: CurriculumPhase | undefined,
): StepState => {
  if (reworkPhase !== undefined && phaseRank(reworkPhase) === index) return "back";
  if (phase === "published") return "done";
  const at = phaseRank(phase);
  return index < at ? "done" : index === at ? "now" : "todo";
};

const STEP_CLASS: Record<StepState, string> = {
  todo: "border-zinc-300 text-zinc-400",
  now: "border-sky-600 bg-sky-100 text-sky-800",
  done: "border-emerald-600 bg-emerald-600 text-white",
  back: "border-dashed border-amber-700 bg-amber-100 text-amber-800",
};

const SLAB_CLASS: Record<StepState, string> = {
  todo: "text-zinc-400",
  now: "text-sky-800",
  done: "text-emerald-800",
  back: "text-amber-800",
};

const Tick = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.4"
    className="h-2.5 w-2.5"
    aria-hidden="true"
  >
    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const trackerLabel = (
  phase: CurriculumPhase,
  reworkPhase: CurriculumPhase | undefined,
): string =>
  reworkPhase !== undefined
    ? `${PHASE_LABEL[phase]}, partly back in ${PHASE_LABEL[reworkPhase]}`
    : PHASE_LABEL[phase];

export const PhaseTracker = ({
  phase,
  reworkPhase,
}: {
  readonly phase: CurriculumPhase;
  readonly reworkPhase?: CurriculumPhase | undefined;
}) => {
  return (
    <span
      role="img"
      className="flex shrink-0 items-center"
      aria-label={trackerLabel(phase, reworkPhase)}
    >
      {CURRICULUM_PHASES.map((stage, index) => {
        const state = stepStateOf(index, phase, reworkPhase);
        // Name the step in progress, the step returned to, and the terminal Published step — so a
        // live document always says it is live, even while part of it is being reworked, and the
        // chain stays narrow enough for a table row and for the rail.
        const named =
          state === "back" ||
          state === "now" ||
          (state === "done" && index === CURRICULUM_PHASES.length - 1);
        return (
          <span key={stage} className="flex items-center">
            {index > 0 ? (
              <span
                className={`h-px w-2.5 ${
                  stepStateOf(index - 1, phase, reworkPhase) === "done"
                    ? "bg-emerald-600"
                    : "bg-zinc-300"
                }`}
              />
            ) : null}
            <span className="flex items-center gap-1.5">
              <span
                title={state === "back" ? `Back in ${PHASE_LABEL[stage]}` : PHASE_LABEL[stage]}
                className={`grid h-[18px] w-[18px] place-items-center rounded-full border-[1.5px] text-[9px] font-semibold ${STEP_CLASS[state]}`}
              >
                {state === "done" ? <Tick /> : index + 1}
              </span>
              {named ? (
                <span className={`text-[11px] font-medium ${SLAB_CLASS[state]}`}>
                  {PHASE_LABEL[stage]}
                </span>
              ) : null}
            </span>
          </span>
        );
      })}
    </span>
  );
};
